// Settings → Expense accounts (V-10): the pack's expense accounts (read-only) and the business's own, under the
// statement groups the trial balance and the profit and loss account show them in. Adding, renaming and retiring the
// business's own run the same commands as chat (#605), with the same refusals; only the buttons the person's Roles
// allow show.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useWords } from "../../i18n/words.js";
import { ListRow, changeProblem } from "../ListRow.js";
import type { ExpenseAccount, ExpenseAccounts as Accounts, ExpenseAccountsApi } from "./api.js";

function AddAccount({
  groups,
  busy,
  onAdd,
}: {
  groups: readonly string[];
  busy: boolean;
  onAdd: (name: string, group: string) => Promise<boolean>;
}) {
  const w = useWords();
  const [name, setName] = useState("");
  const [group, setGroup] = useState(groups.includes("Other expenses") ? "Other expenses" : (groups[0] ?? ""));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (await onAdd(name.trim(), group)) setName("");
  };
  return (
    <form className="card" aria-label={w.accountsAdd} onSubmit={(event) => void submit(event)}>
      <h2>{w.accountsAdd}</h2>
      <label className="field">
        {w.listName} <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
      </label>
      <label className="field">
        {w.accountsGroup}{" "}
        <select value={group} onChange={(event) => setGroup(event.target.value)}>
          {groups.map((g) => (
            <option key={g} value={g} data-pack="">
              {g}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" className="btn btn-go" disabled={busy || name.trim() === "" || group === ""}>
        {w.listAdd}
      </button>
    </form>
  );
}

export function ExpenseAccounts({ api }: { api: ExpenseAccountsApi }) {
  const w = useWords();
  const [shown, setShown] = useState<Accounts>();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [problem, setProblem] = useState<string>();

  const load = useCallback(
    () =>
      api.list().then(
        (accounts) => setShown(accounts),
        (error: unknown) => setProblem(changeProblem(error, w)),
      ),
    [api, w],
  );
  useEffect(() => {
    void load();
  }, [load]);

  /** Runs a change, then shows the accounts as they are after it; false when it was refused. */
  const change = async (run: () => Promise<ExpenseAccount>, said: (account: ExpenseAccount) => string) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      const done = said(await run());
      await load();
      setNotice(done);
      return true;
    } catch (error) {
      setProblem(changeProblem(error, w));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!shown) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.settingsLoading}</p>;
  const { may } = shown;
  const reads = !may.add && !may.rename && !may.retire;
  return (
    <>
      <p className="muted">{w.accountsHelp}</p>
      {reads ? <p className="muted">{w.listReadOnly}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {may.add ? (
        <AddAccount
          groups={shown.groups}
          busy={busy}
          onAdd={(name, group) =>
            change(
              () => api.add({ name, group }),
              (added) => w.listAdded(added.name, added.group),
            )
          }
        />
      ) : null}
      {shown.groups.map((group, i) => {
        const under = shown.accounts.filter((a) => a.group === group);
        if (under.length === 0) return null;
        const headingId = `expense-group-${String(i)}`;
        return (
          <section key={group} className="section" aria-labelledby={headingId}>
            <h2 id={headingId} data-pack="">
              {group}
            </h2>
            <ul className="queue">
              {under.map((account) => (
                <ListRow
                  key={account.code}
                  name={account.name}
                  details={account.own ? w.accountsYours : w.accountsPack}
                  retired={account.retired}
                  // The pack's accounts are read-only: only the pack changes them.
                  mayRename={account.own && may.rename}
                  mayRetire={account.own && may.retire}
                  busy={busy}
                  onRename={(name) =>
                    change(
                      () => api.rename(account.code, name),
                      (renamed) => w.listRenamed(account.name, renamed.name),
                    )
                  }
                  onRetire={() =>
                    change(
                      () => api.retire(account.code),
                      (retired) => w.listRetiredNotice(retired.name),
                    )
                  }
                />
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}
